import { FC, ReactNode, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface TransferSectionCardProps {
  sectionName: string;
  decidedCount: number;
  totalCount: number;
  defaultExpanded?: boolean;
  children: ReactNode;
}

const TransferSectionCard: FC<TransferSectionCardProps> = ({
  sectionName,
  decidedCount,
  totalCount,
  defaultExpanded = true,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const allDone = decidedCount === totalCount;

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      {/* Accordion header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.75,
          cursor: 'pointer',
          bgcolor: 'background.paper',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'grey.100',
          '&:active': { bgcolor: 'grey.50' },
        }}
        onClick={() => setExpanded(!expanded)}
        role="button"
        aria-expanded={expanded}
        aria-label={`${sectionName} section`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {expanded ? (
            <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ fontSize: '0.9375rem' }}
          >
            {sectionName}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1.25,
            py: 0.25,
            borderRadius: '12px',
            fontSize: '0.6875rem',
            fontWeight: 500,
            bgcolor: allDone ? 'success.50' : 'warning.50',
            color: allDone ? 'success.dark' : 'warning.dark',
            border: '1px solid',
            borderColor: allDone ? 'success.200' : 'warning.200',
          }}
        >
          {allDone
            ? `${decidedCount}/${totalCount} ✓`
            : `${decidedCount}/${totalCount}`}
        </Box>
      </Box>

      {/* Collapsible content — no inner padding, proposals handle their own */}
      <Collapse in={expanded}>
        <Box sx={{ bgcolor: 'grey.50' }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export default TransferSectionCard;
